import { useEffect, useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import {
  useGetAllNexusTrackersQuery,
  useGetNexusTrackerSummaryQuery,
  useGetPhysicalNexusTrackerQuery,
  usePopulateBulkSalesTaxInvoiceMutation,
  useUploadBulkSalesTaxInvoiceMutation,
} from "store/apis/salesTax";
import { useDebounce } from "utils/debounce";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import { StateData } from "types/Models/nexusTracker";
import { useWindowSize } from "hooks/useWindowSize";
import { MobileNexusTrackerRow } from "./MobileNexusTrackerRow";
import { TableUI } from "components/design/TableUI";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { EmptySalesTax } from "components/Illustrations/EmptySalesTax";
import { Button } from "components/DesignSystem/Button/Button";
import PrimaryLoader from "components/DesignSystem/Loader/loader";
import { Search } from "components/DesignSystem/Search/Search";
import { BulkInvoiceUpload } from "components/InvoiceList/BulkInvoiceUpload";
import { useModal } from "hooks/useModal";
import { useFilters } from "hooks/useFilter";
import { BULK_SALES_TAX_INVOICE_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { useAnalytics } from "hooks/useAnalytics";
import {
  ADD_INVOICE_IN_BULK,
  SELECT_NEXUS_TRACKER_STATUS,
} from "constants/analyticsEvents";
import { PhysicalNexusTrackerModal } from "./PhysicalNexusTrackerModal";
import classNames from "classnames";
import ToolTip from "components/design/toolTip";
import { NexusTrackerSlider } from "./NexusTrackerSlider";
import { NexusTrackerStatus } from "./NexusTrackerStatus";
import { ActionCell, getDisplayStatus } from "./ActionCell";
import { NexusTrackerSummaryCards } from "./NexusTrackerSummaryCards";
import { NexusTrackerEmptyState } from "./NexusTrackerEmptyState";
import {
  NexusTrackerFilter,
  NexusTrackerFilterValues,
} from "./NexusTrackerFilter";
import { FILING_INFO_MISSING } from "constants/salesTaxRegistrationStatusType";
import { StateCell } from "./StateCell";

const columnHelper = createColumnHelper<StateData>();

export const boolToString: Record<string, string> = {
  true: "yes",
  false: "no",
};

export const statusMapping = {
  MONITORING: "Tracking",
  APPROACHING: "Approaching",
  ALL: "All",
  EXPOSED: "Exceeding",
  REMITTING: "Filing Active",
  EXEMPT: "Not Required",
};

const statusEmptyTextMapping = {
  MONITORING: {
    title: "No sales found",
    description:
      "We'll automatically start tracking as sales get recorded.  Some states may be approaching or exceeding threshold.",
  },
  APPROACHING: {
    title: "No jurisdiction approaching threshold",
    description:
      "We are actively tracking your sales. Any jurisdiction that approaches threshold will move here automatically.",
  },
  EXPOSED: {
    title: "No jurisdiction has exceeded threshold",
    description:
      "We are actively tracking your sales. Any jurisdiction that crosses threshold will move here automatically.",
  },
  ALL: {
    title: "No sales found",
    description:
      "We'll automatically start tracking as sales get recorded.  Some states may be approaching or exceeding threshold.",
  },
  REMITTING: {
    title: "No state is actively filing",
    description:
      "Once a registered state starts its filing cycle, it will show up here automatically.",
  },
  EXEMPT: {
    title: "No state marked as not required",
    description:
      "States with no sales tax applicable to them will show up here automatically.",
  },
};

const highlightCell = ({
  value,
  limit,
}: {
  value: number;
  limit?: number | null;
}) => {
  if (!limit) {
    return "";
  }

  if (value >= limit) {
    return "t-text-red t-text-subtext";
  }

  if (value >= limit * 0.9) {
    return "t-text-orange-80 t-text-subtext";
  }
  return "";
};

export const SalesNexusTracker = () => {
  const entityId = useCurrentEntityId();
  const { path } = useRouteMatch();
  const { link } = useConstructInternalLink();
  const { trackEvent } = useAnalytics();
  const [activeState, setActiveState] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const bulkInvoiceModal = useModal();

  const { values, updateFilter } = useFilters<NexusTrackerFilterValues>({
    initialValue: {
      NEXUS_TYPE: [],
      STATUS_FILTER: [],
    },
    useQueryParams: true,
  });

  const debouncedSearchTerm = useDebounce(searchTerm);

  const query = useQuery();
  const { update } = useUpdateQuery();
  const currentPage = Number(query.get("page")) || 1;

  const {
    data: nexusTrackerData,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetAllNexusTrackersQuery({
    pageNum: currentPage,
    limit: 20,
    entityId,
    status_filter: values.STATUS_FILTER.join(",") || undefined,
    search: debouncedSearchTerm || undefined,
    nexus_type: values.NEXUS_TYPE.join(",") || undefined,
  });

  const handlePageNumChange = (page: number) => {
    update({ query: "page", value: page });
  };

  useEffect(() => {
    if (currentPage !== 1) {
      update({ query: "page", value: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, values.STATUS_FILTER, values.NEXUS_TYPE]);

  const {
    data: physicalNexusTrackerData,
    isSuccess: isPhysicalNexusSuccess,
    isFetching: isPhysicalNexusLoading,
  } = useGetPhysicalNexusTrackerQuery(
    {
      entityId,
    },
    {
      skip: !entityId,
    }
  );

  const { data: nexusTrackerSummary, isLoading: nexusTrackerSummaryLoading } =
    useGetNexusTrackerSummaryQuery(
      {
        entityId,
      },
      {
        skip: !entityId,
      }
    );

  const previouslyCheckedPhysicalNexus =
    boolToString[String(physicalNexusTrackerData?.employees_present)] ||
    boolToString[String(physicalNexusTrackerData?.warehouse_present)] ||
    boolToString[String(physicalNexusTrackerData?.office_present)];

  const toggleStatusFilter = (status: keyof typeof statusMapping) => {
    trackEvent(SELECT_NEXUS_TRACKER_STATUS, {
      status: status.toLowerCase(),
    });
    const nextValues = values.STATUS_FILTER.includes(status)
      ? values.STATUS_FILTER.filter((value) => value !== status)
      : [...values.STATUS_FILTER, status];
    updateFilter("STATUS_FILTER", nextValues);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("jurisdiction", {
        header: "State",
        cell: (info) => {
          const rowData = info.row.original;
          const isExempted = !Boolean(
            rowData.transaction_amount_limit || rowData.transaction_count_limit
          );

          return (
            <StateCell
              jurisdiction={info.getValue()}
              nexusType={rowData.nexus_type}
              isExempted={isExempted}
            />
          );
        },
        size: 30,
      }),
      columnHelper.accessor("transactions_amount", {
        header: "Sales",
        cell: (info) => {
          const salesLimit = info.row.original.transaction_amount_limit;
          return (
            <div className="t-text-right t-flex t-items-center t-gap-1">
              <div
                className={classNames(
                  highlightCell({
                    value: info.getValue(),
                    limit: salesLimit,
                  })
                )}
              >
                <AmountSuperScript amount={info.getValue()} />
              </div>{" "}
              <span className="t-text-text-30 t-text-body-sm">
                /{" "}
                {Boolean(info.row.original.transaction_amount_limit) ? (
                  <AmountSuperScript
                    amount={info.row.original.transaction_amount_limit ?? 0}
                  />
                ) : (
                  "No limit"
                )}
              </span>
            </div>
          );
        },
        size: 20,
      }),
      columnHelper.accessor("transaction_count", {
        header: "Transactions",
        cell: (info) => {
          return (
            <div className="t-flex t-gap-x-1 t-items-center">
              <span
                className={classNames(
                  highlightCell({
                    value: info.getValue(),
                    limit: info.row.original.transaction_count_limit,
                  })
                )}
              >
                {info.getValue()}
              </span>

              <span className="t-text-text-30 t-text-body-sm">
                / {info.row.original.transaction_count_limit || "N/A"}
              </span>
            </div>
          );
        },
        size: 15,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const rowData = info.row.original;

          return (
            <NexusTrackerStatus
              status={getDisplayStatus(rowData)}
              isExempted={
                !Boolean(
                  rowData.transaction_amount_limit ||
                    rowData.transaction_count_limit
                )
              }
            />
          );
        },
        size: 10,
      }),
      columnHelper.display({
        id: "actions",
        header: " ",
        cell: (info) => {
          const hasNoThresholds = !Boolean(
            info.row.original.transaction_amount_limit ||
              info.row.original.transaction_count_limit
          );
          const hasActiveRegistration =
            Boolean(info.row.original.state_registration_status) ||
            info.row.original.registration_status === FILING_INFO_MISSING;
          const isExempted = hasNoThresholds && !hasActiveRegistration;

          return (
            <ActionCell
              isExempted={isExempted}
              rowData={info.row.original}
              onOpenSlider={setActiveState}
            />
          );
        },
        size: 10,
      }),
    ],
    []
  );

  const statesData = nexusTrackerData?.states_data ?? [];
  const emptyStateText =
    statusEmptyTextMapping[values.STATUS_FILTER[0]] ??
    statusEmptyTextMapping.ALL;
  const isTrulyEmpty = nexusTrackerSummary?.tracked_count === 0;

  const table = useReactTable({
    data: statesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { isMobile } = useWindowSize();

  const openSlider = (row: Row<StateData>) => {
    setActiveState(row.original.jurisdiction);
  };

  const statesList = isMobile ? (
    <div className="t-flex t-flex-col t-gap-3 t-overflow-auto t-h-full t-pb-20">
      {table.getRowModel().rows.map((row) => (
        <MobileNexusTrackerRow key={row.id} row={row} onClick={openSlider} />
      ))}
    </div>
  ) : (
    <TableUI
      onRowClick={openSlider}
      sticky={false}
      table={table}
      isHeaderCellUpperCase={false}
      size="regular"
      className="t-overflow-auto t-h-full t-pb-20 scrollbar-visible"
    />
  );

  const activeStateDetails = nexusTrackerData?.states_data.find(
    (state) => state.jurisdiction === activeState
  );

  const nexusTrackerModal = useModal();
  const [physicalPresenceStateId, setPhysicalPresenceStateId] = useState<
    string | null
  >(null);

  const handlePhysicalNexus = () => {
    setPhysicalPresenceStateId(null);
    nexusTrackerModal.open();
  };

  const handleEditPhysicalPresenceForState = (stateId: string) => {
    setPhysicalPresenceStateId(stateId);
    nexusTrackerModal.open();
  };

  const physicalPresenceButton = isPhysicalNexusSuccess && (
    <ToolTip
      text={
        previouslyCheckedPhysicalNexus ? (
          <p className="t-m-0">
            If your physical presence in the US has changed,
            <br /> update here to stay tax compliant
          </p>
        ) : (
          <p className="t-m-0">
            Check if your business has a physical nexus in <br />
            US to avoid government notices.
          </p>
        )
      }
    >
      <Button size="small" onClick={handlePhysicalNexus}>
        {isPhysicalNexusLoading
          ? "Physical presence..."
          : "Update physical presence"}
      </Button>
    </ToolTip>
  );

  return (
    <DashboardLayout
      header={
        <Header v2 title="Nexus Tracker" right={physicalPresenceButton} />
      }
    >
      <Async.Root
        isLoading={isLoading || nexusTrackerSummaryLoading}
        isEmpty={false}
        isSuccess={isSuccess}
      >
        <Async.Success>
          <DashboardContainer>
            <DashboardContainer.Content>
              {isTrulyEmpty ? (
                <NexusTrackerEmptyState
                  onCheckPhysicalPresence={handlePhysicalNexus}
                />
              ) : (
                <>
                  {nexusTrackerSummary && (
                    <div className="t-pb-4">
                      <NexusTrackerSummaryCards
                        summary={nexusTrackerSummary}
                        registrationsUrl={link(`${path}/registrations`)}
                        onFilterByStatus={toggleStatusFilter}
                      />
                    </div>
                  )}

                  <div className="t-flex t-items-center t-gap-2 t-pb-4 sm:t-flex-wrap">
                    <Search
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                      className="sm:t-w-full"
                    />

                    <NexusTrackerFilter
                      values={values}
                      updateFilter={updateFilter}
                      onToggleStatus={toggleStatusFilter}
                    />

                    <div className="t-grow t-ml-auto t-shrink-0 t-flex t-justify-end">
                      <PaginationHandler
                        page_num={nexusTrackerData?.current_page || 1}
                        per_page={nexusTrackerData?.per_page || 1}
                        total_count={nexusTrackerData?.total_count || 0}
                        total_pages={nexusTrackerData?.total_pages || 1}
                        onPageNumChange={handlePageNumChange}
                        isLoading={isFetching}
                      />
                    </div>
                  </div>

                  {!isFetching && (
                    <div className="t-flex t-flex-col t-flex-1 t-min-h-0 t-overflow-hidden">
                      {statesData.length === 0 ? (
                        <div className="t-h-full t-flex t-flex-col t-items-center t-justify-center">
                          <div className="t-mt-40">
                            <EmptySalesTax />
                          </div>
                          <>
                            <p className="t-text-text-100 t-text-body-sm t-mb-0 t-mt-6">
                              {emptyStateText.title}
                            </p>
                            <p className="t-text-text-30 t-text-body-sm t-mb-0 t-mt-1 t-text-center t-max-w-[450px]">
                              {emptyStateText.description}
                            </p>
                          </>
                        </div>
                      ) : (
                        statesList
                      )}
                    </div>
                  )}
                </>
              )}
              {isFetching && <PrimaryLoader />}
            </DashboardContainer.Content>
          </DashboardContainer>
        </Async.Success>
      </Async.Root>

      <BulkInvoiceUpload
        isOpen={bulkInvoiceModal.isOpen}
        close={bulkInvoiceModal.close}
        usePopulateMutation={usePopulateBulkSalesTaxInvoiceMutation}
        useUploadMutation={useUploadBulkSalesTaxInvoiceMutation}
        templateUrl={BULK_SALES_TAX_INVOICE_EXCEL_TEMPLATE_URL}
      />

      <PhysicalNexusTrackerModal
        isOpen={nexusTrackerModal.isOpen}
        onClose={nexusTrackerModal.close}
        hasAlreadyCheckedNexus={Boolean(previouslyCheckedPhysicalNexus)}
        stateId={physicalPresenceStateId}
      />

      {activeStateDetails && (
        <NexusTrackerSlider
          setActiveState={setActiveState}
          activeState={activeState}
          activeStateDetails={activeStateDetails}
          onEditPhysicalPresence={handleEditPhysicalPresenceForState}
        />
      )}
    </DashboardLayout>
  );
};
